import { showToast } from '../../../utils/toast.util';
/**
 * WorkoutRoutineForm Service
 * Implements communication layer for workout routine mutations and queries
 * Requirements: 5.1, 7.1
 */

import {client} from '../../../../core/stores';
import {
  MUTATION_WORKOUT_GROUP_ROUTINE,
  MUTATION_WORKOUT_GROUP_BY_ROUTINE,
} from '../../../../core/graphql/types';
import {
  transformToMutation,
  transformFromResponse,
  transformWorkoutGroupToMutation,
  mapBackendErrors,
  type WorkoutRoutineResponse,
  type GraphQLError,
} from './WorkoutRoutineForm.map';
import type {
  WorkoutRoutineFormData,
  WorkoutGroupFormData,
} from './WorkoutRoutineForm.schema';

// Declare Materialize toast for notifications
declare const M: {
  toast: (options: {html: string; displayLength?: number}) => void;
};

// ============================================================================
// Service Response Types
// ============================================================================

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  errors?: Record<string, string>;
  message?: string;
}

export interface SaveWorkoutRoutineOptions {
  /** Callback for progress updates (0-100) */
  onProgress?: (progress: number) => void;
  /** Whether to show toast notifications */
  showNotifications?: boolean;
  /** Whether this is a draft save */
  isDraft?: boolean;
}

export interface SaveWorkoutGroupOptions {
  /** Callback for progress updates (0-100) */
  onProgress?: (progress: number) => void;
  /** Whether to show toast notifications */
  showNotifications?: boolean;
}

// ============================================================================
// Workout Routine Service Functions
// ============================================================================

/**
 * Save a workout routine (create or update)
 * Wrapper for MUTATION_WORKOUT_GROUP_ROUTINE
 * Requirements: 5.1 - Transform data to Rails nested attributes format
 * Requirements: 7.1 - Maintain backward compatibility with existing GraphQL mutations
 *
 * @param formData - The form data to save
 * @param options - Optional configuration for the save operation
 * @returns Promise with the result of the save operation
 */
export async function saveWorkoutRoutine(
  formData: WorkoutRoutineFormData,
  options: SaveWorkoutRoutineOptions = {},
): Promise<ServiceResult<WorkoutRoutineFormData>> {
  const {onProgress, showNotifications = true, isDraft} = options;

  try {
    // Report initial progress
    onProgress?.(10);

    // Transform form data to mutation input
    const mutationInput = transformToMutation(formData);

    // Apply isDraft override if specified
    if (isDraft !== undefined) {
      mutationInput.isDraft = isDraft;
    } else {
      // Auto-determine draft status based on workout groups
      mutationInput.isDraft = (formData.workoutGroups || []).length === 0;
    }

    // Report transformation complete
    onProgress?.(30);

    // Prepare variables for mutation (excluding workoutGroups for routine-only save)
    const variables: Record<string, unknown> = {
      id: mutationInput.id,
      name: mutationInput.name || 'Nova rotina de treino',
      startDate: mutationInput.startDate,
      endDate: mutationInput.endDate,
      cycleType: mutationInput.cycleType,
      isDraft: mutationInput.isDraft,
      showOnlyOnScheduledDays: mutationInput.showOnlyOnScheduledDays,
      target: mutationInput.target,
      commentEnable: mutationInput.commentEnable,
      programIds: mutationInput.programIds,
      usersTargetIds: mutationInput.usersTargetIds,
      weekDayIds: mutationInput.weekDayIds,
    };

    // Execute mutation
    onProgress?.(50);

    const response = await client('account').mutate({
      mutation: MUTATION_WORKOUT_GROUP_ROUTINE,
      variables,
    });

    onProgress?.(80);

    // Extract response data
    const {data} = response;
    const workoutGroupRoutine = data?.workoutGroupRoutine?.workoutGroupRoutine;

    if (!workoutGroupRoutine) {
      throw new Error('No data returned from mutation');
    }

    // Transform response back to form data
    const resultData = transformFromResponse(
      workoutGroupRoutine as WorkoutRoutineResponse,
    );

    // Show success notification
    if (showNotifications) {
      showToast({html: 'Sua rotina foi salva com sucesso!'});
    }

    onProgress?.(100);

    return {
      success: true,
      data: resultData,
    };
  } catch (error: unknown) {
    // Handle GraphQL errors
    if (isGraphQLError(error)) {
      const graphqlErrors = error.graphQLErrors || [];
      const fieldErrors = mapBackendErrors(graphqlErrors as GraphQLError[]);
      const errorMessage = graphqlErrors
        .map((e: {message: string}) => e.message)
        .join(', ');

      if (showNotifications) {
        showToast({html: 'Houve um erro na sua solicitação.'});
        if (errorMessage) {
          showToast({html: errorMessage});
        }
      }

      return {
        success: false,
        errors: fieldErrors,
        message: errorMessage || 'Erro ao salvar rotina',
      };
    }

    // Handle network or other errors
    const message =
      error instanceof Error ? error.message : 'Erro desconhecido';

    if (showNotifications) {
      showToast({html: 'Houve um erro na sua solicitação.'});
    }

    return {
      success: false,
      errors: {_form: message},
      message,
    };
  }
}

/**
 * Save a single workout group within a routine
 * Wrapper for MUTATION_WORKOUT_GROUP_BY_ROUTINE
 * Requirements: 5.1 - Transform data to Rails nested attributes format
 * Requirements: 7.1 - Maintain backward compatibility with existing GraphQL mutations
 *
 * @param routineId - The ID of the parent workout routine
 * @param workoutGroup - The workout group data to save
 * @param groupIndex - The index of the workout group (for ordering)
 * @param options - Optional configuration for the save operation
 * @returns Promise with the result of the save operation
 */
export async function saveWorkoutGroup(
  routineId: string | number,
  workoutGroup: WorkoutGroupFormData,
  groupIndex: number,
  options: SaveWorkoutGroupOptions = {},
): Promise<ServiceResult<WorkoutGroupFormData>> {
  const {onProgress, showNotifications = true} = options;

  try {
    // Report initial progress
    onProgress?.(10);

    // Transform workout group to mutation input
    const mutationInput = transformWorkoutGroupToMutation(
      workoutGroup,
      groupIndex,
    );

    // Report transformation complete
    onProgress?.(30);

    // Prepare variables for mutation
    const variables = {
      workoutGroupRoutineId: routineId,
      id: mutationInput.id,
      name: mutationInput.name,
      coverUrl: mutationInput.coverUrl,
      videoUrl: mutationInput.videoUrl,
      audioUrl: mutationInput.audioUrl,
      attachmentUrl: mutationInput.attachmentUrl,
      workoutsAttributes: mutationInput.workoutsAttributes,
      _destroy: mutationInput._destroy,
    };

    // Execute mutation
    onProgress?.(50);

    const response = await client('account').mutate({
      mutation: MUTATION_WORKOUT_GROUP_BY_ROUTINE,
      variables,
    });

    onProgress?.(80);

    // Extract response data
    const {data} = response;
    const savedWorkoutGroup = data?.workoutGroupByRoutine?.workoutGroup;

    if (!savedWorkoutGroup) {
      throw new Error('No data returned from mutation');
    }

    // Transform response back to form data
    const resultData: WorkoutGroupFormData = {
      id: savedWorkoutGroup.id,
      name: savedWorkoutGroup.name,
      description: savedWorkoutGroup.description,
      workouts: (savedWorkoutGroup.workouts || []).map(
        (workout: Record<string, unknown>) => ({
          id: workout.id,
          name: workout.name,
          description: workout.description,
          time_text: workout.timeText,
          checkinEnabled: workout.checkinEnabled ?? false,
          exerciseWorkouts: (
            (workout.exerciseWorkouts as Array<Record<string, unknown>>) || []
          ).map((exercise: Record<string, unknown>) => ({
            id: exercise.id,
            exerciseId:
              exercise.exerciseId ||
              (exercise.exercise as Record<string, unknown>)?.id,
            exercise: exercise.exercise
              ? {
                  value:
                    (exercise.exercise as Record<string, unknown>).id ||
                    (exercise.exercise as Record<string, unknown>).value,
                  label:
                    (exercise.exercise as Record<string, unknown>).name ||
                    (exercise.exercise as Record<string, unknown>).label,
                }
              : undefined,
            repetitions: exercise.repetitions,
            notes: exercise.notes,
            rounds: exercise.rounds,
            load: exercise.load,
            time: exercise.time,
            timeText: exercise.timeText,
            restTime: exercise.restTime,
          })),
        }),
      ),
      coverUrl: savedWorkoutGroup.cover?.url,
      videoUrl: savedWorkoutGroup.video?.url,
      audioUrl: savedWorkoutGroup.audio?.url,
      attachmentUrl: savedWorkoutGroup.attachment?.url,
      cover: savedWorkoutGroup.cover,
      video: savedWorkoutGroup.video,
      audio: savedWorkoutGroup.audio,
      attachment: savedWorkoutGroup.attachment,
    };

    // Show success notification
    if (showNotifications) {
      showToast({html: `Treino ${savedWorkoutGroup.name} salvo com sucesso!`});
    }

    onProgress?.(100);

    return {
      success: true,
      data: resultData,
    };
  } catch (error: unknown) {
    // Handle GraphQL errors
    if (isGraphQLError(error)) {
      const graphqlErrors = error.graphQLErrors || [];
      const fieldErrors = mapBackendErrors(graphqlErrors as GraphQLError[]);
      const errorMessage = graphqlErrors
        .map((e: {message: string}) => e.message)
        .join(', ');

      if (showNotifications) {
        showToast({html: 'Houve um erro na sua solicitação.'});
        if (errorMessage) {
          showToast({html: errorMessage});
        }
      }

      return {
        success: false,
        errors: fieldErrors,
        message: errorMessage || 'Erro ao salvar treino',
      };
    }

    // Handle network or other errors
    const message =
      error instanceof Error ? error.message : 'Erro desconhecido';

    if (showNotifications) {
      showToast({html: 'Houve um erro na sua solicitação.'});
    }

    return {
      success: false,
      errors: {_form: message},
      message,
    };
  }
}

/**
 * Save all workout groups in a routine sequentially
 * Useful for saving the entire form with progress tracking
 *
 * @param routineId - The ID of the parent workout routine
 * @param workoutGroups - Array of workout groups to save
 * @param options - Optional configuration for the save operation
 * @returns Promise with the results of all save operations
 */
export async function saveAllWorkoutGroups(
  routineId: string | number,
  workoutGroups: WorkoutGroupFormData[],
  options: SaveWorkoutGroupOptions = {},
): Promise<ServiceResult<WorkoutGroupFormData[]> & {indexMap?: Map<number, WorkoutGroupFormData>}> {
  const {onProgress, showNotifications = true} = options;
  const results: WorkoutGroupFormData[] = [];
  const indexMap = new Map<number, WorkoutGroupFormData>();
  const errors: Record<string, string> = {};
  let hasErrors = false;

  const totalGroups = workoutGroups.length;

  for (let i = 0; i < workoutGroups.length; i++) {
    const group = workoutGroups[i];

    if (group._destroy && !group.id) {
      continue;
    }

    // Calculate progress for this group
    const groupProgress = (progress: number) => {
      const baseProgress = (i / totalGroups) * 100;
      const groupContribution = (progress / 100) * (100 / totalGroups);
      onProgress?.(Math.round(baseProgress + groupContribution));
    };

    const result = await saveWorkoutGroup(routineId, group, i, {
      onProgress: groupProgress,
      showNotifications: false,
    });

    if (result.success && result.data) {
      results.push(result.data);
      indexMap.set(i, result.data);
    } else {
      hasErrors = true;
      if (result.errors) {
        Object.entries(result.errors).forEach(([key, value]) => {
          errors[`workoutGroups.${i}.${key}`] = value;
        });
      }
    }
  }

  if (showNotifications) {
    if (hasErrors) {
      showToast({html: 'Alguns treinos não foram salvos. Verifique os erros.'});
    } else {
      showToast({html: 'Todos os treinos foram salvos com sucesso!'});
    }
  }

  return {
    success: !hasErrors,
    data: results,
    indexMap,
    errors: hasErrors ? errors : undefined,
  };
}

/**
 * Load a workout routine by ID
 * Note: This function assumes the data is already loaded via the page props
 * In a future iteration, this could be expanded to fetch data via GraphQL query
 *
 * @param routineData - The routine data from page props
 * @returns The transformed form data
 */
export function loadWorkoutRoutine(
  routineData: WorkoutRoutineResponse,
): WorkoutRoutineFormData {
  return transformFromResponse(routineData);
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Type guard to check if an error is a GraphQL error
 */
function isGraphQLError(
  error: unknown,
): error is {graphQLErrors: Array<{message: string}>} {
  return (
    typeof error === 'object' &&
    error !== null &&
    'graphQLErrors' in error &&
    Array.isArray((error as {graphQLErrors: unknown}).graphQLErrors)
  );
}

/**
 * Validate form data before submission
 * Returns validation errors if any
 */
export function validateBeforeSubmit(
  formData: WorkoutRoutineFormData,
): Record<string, string> | null {
  const errors: Record<string, string> = {};

  // Validate target selection
  if (
    formData.target === 'users' &&
    (!formData.usersTargetIds || formData.usersTargetIds.length === 0)
  ) {
    errors.target =
      'Se a rotina de treino é para uma pessoa ou grupo, você precisa selecionar um ou mais usuários';
  }

  // Validate workout groups
  if (formData.workoutGroups && formData.workoutGroups.length > 0) {
    formData.workoutGroups.forEach((group, groupIndex) => {
      if (group._destroy) return;

      // Validate workouts within group
      if (group.workouts && group.workouts.length > 0) {
        group.workouts.forEach((workout, workoutIndex) => {
          if (workout._destroy) return;

          // Workout must have description or exercises
          const hasDescription =
            workout.description && workout.description.trim().length > 0;
          const hasExercises =
            workout.exerciseWorkouts &&
            workout.exerciseWorkouts.filter((e) => !e._destroy).length > 0;

          if (!hasDescription && !hasExercises) {
            errors[
              `workoutGroups.${groupIndex}.workouts.${workoutIndex}.description`
            ] = 'O bloco precisa ter uma descrição ou pelo menos um exercício';
          }

          // Validate time format if provided
          if (workout.time_text && !isValidTimeFormat(workout.time_text)) {
            errors[
              `workoutGroups.${groupIndex}.workouts.${workoutIndex}.time_text`
            ] = 'Formato de tempo inválido. Use MM:SS';
          }
        });
      }
    });
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

/**
 * Validate time format (MM:SS)
 */
function isValidTimeFormat(time: string): boolean {
  if (!time) return true;

  const parts = time.split(':');
  if (parts.length <= 1) return false;

  const numericPart = time.replace(/[^0-9]/g, '');
  return numericPart.length >= 3;
}
